import {
  Archive,
  Building2,
  Database,
  FolderOpen,
  Home,
  Layers,
  LifeBuoy,
  Lock,
  Network,
  Plug,
  Zap,
} from 'lucide-react'
import { useMemo } from 'react'
import { Link, useLocation } from 'react-router'
import { SidebarPopoverMenu } from '@/components/layout/sidebar-popover-menu'
import { SidebarStatusIndicator } from '@/components/layout/sidebar-status-indicator'
import { SidebarUserNav } from '@/components/layout/sidebar-user-nav'
import {
  useHelpItems,
  useSettingsItems,
  useToolsItems,
} from '@/components/layout/sidebar-utility-items'
import { useResourceContext } from '@/components/layout/use-resource-context'
import { usePaymentMethodGate } from '@/components/payment-method-gate/context'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from '@/components/sv/sidebar'
import { useCompanyPath, useIdCompany, useRBAC } from '@/lib/hooks'
import { RoleID, hasProjectLevelRole } from '@/lib/rbac'
import { SevallaLogo } from '@/logos'

export const PrimarySidebar = () => {
  const { isMobile } = useSidebar()
  const resource = useResourceContext()
  const { idCompany } = useIdCompany()
  const { getCompanyPath } = useCompanyPath()
  const { rbac, rolesInCompany, totalServices } = useRBAC()
  const isPaymentGated = usePaymentMethodGate()
  const location = useLocation()

  const companyRbac = useMemo(() => rbac.company(idCompany), [idCompany, rbac])
  const toolsItems = useToolsItems()
  const settingsItems = useSettingsItems()
  const helpItems = useHelpItems()

  const items = useMemo(
    () =>
      (
        [
          {
            title: 'Home',
            url: '/',
            icon: Home,
            hasAccess:
              companyRbac.apps.view.can() &&
              companyRbac.databases.view.can() &&
              companyRbac.staticSites.view.can() &&
              companyRbac.objectStorages.view.can(),
            activeTags: [],
          },
          {
            title: 'Projects',
            url: '/projects',
            icon: FolderOpen,
            hasAccess:
              totalServices.totalProjects > 0 ||
              companyRbac.projects.view.can(),
            activeTags: ['/projects', '/project/'],
          },
          {
            title: 'Applications',
            url: '/apps',
            icon: Layers,
            hasAccess:
              companyRbac.apps.view.can() ||
              rolesInCompany.includes(RoleID.APP_ADMIN) ||
              hasProjectLevelRole(rolesInCompany),
            activeTags: [
              '/app/',
              '/pipeline/',
              '/apps',
              '/apps/create',
              '/apps/create-pipeline',
            ],
          },
          {
            title: 'Databases',
            url: '/databases',
            icon: Database,
            hasAccess:
              companyRbac.databases.view.can() ||
              rolesInCompany.includes(RoleID.DATABASE_ADMIN) ||
              hasProjectLevelRole(rolesInCompany),
            activeTags: ['/database/', '/databases'],
          },
          {
            title: 'Static sites',
            url: '/static-sites',
            icon: Zap,
            hasAccess:
              companyRbac.staticSites.view.can() ||
              rolesInCompany.includes(RoleID.STATIC_SITE_ADMIN) ||
              hasProjectLevelRole(rolesInCompany),
            activeTags: ['/static-site/', '/static-sites'],
          },
          {
            title: 'Object storage',
            url: '/object-storage',
            icon: Archive,
            hasAccess:
              companyRbac.objectStorages.view.can() ||
              rolesInCompany.includes(RoleID.OBJECT_STORAGE_ADMIN) ||
              hasProjectLevelRole(rolesInCompany),
            activeTags: ['/object-storage'],
          },
          {
            title: 'Load balancers',
            url: '/load-balancers',
            icon: Network,
            hasAccess:
              companyRbac.loadBalancers.view.can() ||
              rolesInCompany.includes(RoleID.LOAD_BALANCER_ADMIN) ||
              hasProjectLevelRole(rolesInCompany),
            activeTags: ['/load-balancer/', '/load-balancers'],
          },
        ] as const
      ).map((item) => ({
        ...item,
        hasAccess: isPaymentGated ? false : item.hasAccess,
      })),
    [companyRbac, rolesInCompany, totalServices.totalProjects, isPaymentGated]
  )

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="h-[3.25rem] shrink-0 border-b border-sidebar-border flex-row items-center pl-2.5">
        <Link
          to={
            hasProjectLevelRole(rolesInCompany)
              ? getCompanyPath({ pathname: '/projects' })
              : getCompanyPath({ pathname: '/' })
          }
          aria-label="Sevalla home"
          className="flex items-center"
        >
          <div className="overflow-hidden h-7 w-[5.5rem] group-data-[collapsible=icon]:w-7 transition-[width] duration-200 ease-linear">
            <SevallaLogo className="h-7 w-[5.5rem] max-w-none" />
          </div>
        </Link>
      </SidebarHeader>

      <SidebarContent className="pt-1">
        {isMobile && resource ? (
          <SidebarGroup>
            <SidebarGroupLabel>{resource.type}</SidebarGroupLabel>
            <SidebarGroupContent>{resource.node}</SidebarGroupContent>
          </SidebarGroup>
        ) : null}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {items.map((item) => {
                const isActive =
                  item.url === '/'
                    ? location.pathname === '/'
                    : item.activeTags.some((tag) =>
                        location.pathname.includes(tag)
                      )
                const Icon = item.hasAccess ? item.icon : Lock
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      isActive={isActive}
                      tooltip={item.title}
                      disabled={!item.hasAccess}
                    >
                      {item.hasAccess ? (
                        <Link
                          to={getCompanyPath({ pathname: item.url })}
                          aria-current={isActive ? 'page' : undefined}
                        >
                          <Icon />
                          <div className="overflow-hidden flex-1 transition-[max-width] duration-200 ease-linear max-w-40 group-data-[collapsible=icon]:max-w-0">
                            <span className="whitespace-nowrap">
                              {item.title}
                            </span>
                          </div>
                        </Link>
                      ) : (
                        <div>
                          <Icon />
                          <div className="overflow-hidden flex-1 transition-[max-width] duration-200 ease-linear max-w-40 group-data-[collapsible=icon]:max-w-0">
                            <span className="whitespace-nowrap">
                              {item.title}
                            </span>
                          </div>
                        </div>
                      )}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="shrink-0">
        <SidebarMenu>
          <SidebarPopoverMenu
            label="Integration"
            icon={Plug}
            items={toolsItems}
          />
          <SidebarPopoverMenu
            label="Company settings"
            icon={Building2}
            items={settingsItems}
          />
          <SidebarPopoverMenu label="Help" icon={LifeBuoy} items={helpItems} />
          <SidebarStatusIndicator />
          <SidebarUserNav />
        </SidebarMenu>
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  )
}
